import { useState, useEffect, useCallback, useRef } from 'react';
import { api, Folder, FolderWithMeta, FolderRekeyStatus, FileUpload, UploadSearchScope } from '../../api';
import { LoadingSpinner } from '../ui';
import { Breadcrumb } from './Breadcrumb';
import { FolderList } from './FolderList';
import { FileList } from './FileList';
import { FileDetailsPanel } from './FileDetailsPanel';
import { FolderDescription } from './FolderDescription';
import { useSelection } from './useSelection';
import { useUpload } from './useUpload';
import { UploadPanels } from './UploadPanels';
import { DeleteConfirmModals } from './DeleteConfirmModals';
import { resolveDropTarget } from './dropTarget';
import { ALLOWED_EXTENSIONS } from '../../utils/fileHash';
import { chunk, mapWithConcurrency } from '../../utils/concurrency';
import { getErrorMessage, usePaginatedList, useDebouncedValue, busyTracker } from '../../../../shared/src';
import { UploadIcon, FolderPlusIcon, FileIcon, FolderIcon } from '../ui/icons/UploadIcons';
import './FileBrowser.css';

interface FileBrowserProps {
  currentFolderId: string | null;
  onFolderChange: (folderId: string | null) => void;
  rootFolderId?: string | null;
  projectId: string;
}

// Parallel move fan-out for multi-item drag-and-drop (bounded so we don't
// flood the API / WAF). Mirrors the bulk-upload concurrency style.
const MOVE_CONCURRENCY = 6;

// Safety cap on post-move re-key rounds (~250 files each, so ~100k files). The
// server errors on a round that makes no progress, so this only guards against
// a pathological total, not an infinite retry loop.
const REKEY_MAX_ROUNDS = 400;

// Attempts per re-key chunk. One transient S3/DB blip on the last chunk must
// not abort a multi-thousand-file relocation as a terminal error.
const REKEY_CHUNK_ATTEMPTS = 3;

export function FileBrowser({ currentFolderId, onFolderChange, rootFolderId, projectId }: FileBrowserProps) {
  // When at the project root, every read AND write must target the project's
  // __root__ folder (rootFolderId). Passing raw null for writes orphans the
  // row at parent_id/folder_id = NULL, which the listing query then misses.
  const effectiveFolderId = currentFolderId ?? rootFolderId ?? null;

  // Filename search (component-local; intentionally not persisted to the URL hash in v1).
  const [searchRaw, setSearchRaw] = useState('');
  const [scope, setScope] = useState<UploadSearchScope>('folder');
  const debouncedSearch = useDebouncedValue(searchRaw, 300);
  // Collapse to '' the instant the box is empty (don't wait for the debounce) so
  // clearing the search / navigating takes effect in the same render. Otherwise
  // require >= 2 chars: a 1-char term matches almost everything. (A 2-char term
  // isn't trigram-index-backed — it falls back to a scope-bounded filter scan,
  // which is acceptable; 3+ chars use the GIN trigram index idx_files_original_name_trgm.)
  const effectiveQ =
    searchRaw.trim() === '' ? '' : debouncedSearch.trim().length >= 2 ? debouncedSearch.trim() : '';
  const isSearching = effectiveQ !== '';
  // Scope only takes effect while actively searching; an empty box is always folder
  // scope, so toggling "entire project" (or clearing the box) never flattens the view.
  const isProjectScope = isSearching && scope === 'project';

  // Navigation state (path derived from currentFolderId prop)
  const [currentPath, setCurrentPath] = useState<Folder[]>([]);
  const [currentFolderDescription, setCurrentFolderDescription] = useState<string | null>(null);

  // Content state
  const [folders, setFolders] = useState<FolderWithMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Non-null while a folder move is in flight; drives the "Moving…" overlay so a
  // slow move (it rewrites every descendant file's S3 key server-side) doesn't look
  // like nothing happened and prompt the user to re-drag.
  const [movingMessage, setMovingMessage] = useState<string | null>(null);

  // File pagination. `files` is the *cumulative* loaded set across pages (Load
  // More appends, deduped by id). `useSelection` prunes against
  // `files.map(f => f.id)`, so prior-page selections survive appends.
  const fetchFilesPage = useCallback(
    async (page: number) => {
      const result = await api.listUploads(
        projectId,
        // Project scope searches every folder, so don't constrain by the current one.
        isProjectScope ? undefined : (effectiveFolderId ?? undefined),
        { page, q: effectiveQ || undefined, scope: isProjectScope ? 'project' : 'folder' },
      );
      return { items: result.uploads, pagination: result.pagination };
    },
    [projectId, effectiveFolderId, effectiveQ, isProjectScope],
  );
  // Folding scope + query into scopeKey makes a new search reset to page 1 (via the
  // loadContents effect below) and invalidates any in-flight Load More. The same key
  // guards the component-owned page-1 and select-all fetches (see scopeKeyRef).
  const scopeKey = `${isProjectScope ? 'project' : (effectiveFolderId ?? 'root')}|${effectiveQ}`;
  const filePagination = usePaginatedList<FileUpload>({
    scopeKey,
    fetchPage: fetchFilesPage,
  });
  const files = filePagination.items;

  // Full set of folder file ids materialized by a "Select all" (spans unloaded pages).
  // Supplied to useSelection as `selectableIds` so those ids survive pagination pruning.
  const [selectableFileIds, setSelectableFileIds] = useState<Set<string> | null>(null);
  // A folder change — or a change in search term/scope — invalidates the
  // materialized id universe so a stale "select all" set can't be reused.
  // Keyed on isProjectScope (which mirrors scopeKey) so toggling the scope
  // checkbox with an empty box — a no-op for the view — doesn't drop it.
  useEffect(() => {
    setSelectableFileIds(null);
  }, [effectiveFolderId, effectiveQ, isProjectScope]);

  // Selection state
  const [selectedFile, setSelectedFile] = useState<FileUpload | null>(null);

  // Drag state (tracking for potential future visual feedback)
  const [, setDraggingFile] = useState<FileUpload | null>(null);

  // Modal state
  const [deleteTarget, setDeleteTarget] = useState<
    { type: 'folder'; item: FolderWithMeta } | { type: 'file'; item: FileUpload } | null
  >(null);
  // True while the single folder/file delete request is in flight, so the modal
  // can show progress and disable its buttons instead of looking frozen.
  const [deletingTarget, setDeletingTarget] = useState(false);

  // Inline folder creation state (for toolbar button)
  const [isCreatingNewFolder, setIsCreatingNewFolder] = useState(false);

  // Multi-select
  const fileSelection = useSelection({
    itemIds: files.map((f) => f.id),
    currentFolderId,
    selectableIds: selectableFileIds,
  });
  const folderSelection = useSelection({
    itemIds: folders.map((f) => f.id),
    currentFolderId,
  });

  // A search-term or scope change replaces which files are shown, so drop any active
  // multi-selection (mirrors useSelection's reset on folder navigation) — otherwise a
  // stale selection could feed a bulk action on files no longer in view.
  const { clearSelection: clearFileSelection } = fileSelection;
  const { clearSelection: clearFolderSelection } = folderSelection;
  useEffect(() => {
    clearFileSelection();
    clearFolderSelection();
  }, [effectiveQ, isProjectScope, clearFileSelection, clearFolderSelection]);

  // Bulk delete state
  const [bulkDeleteTarget, setBulkDeleteTarget] = useState<{
    type: 'files' | 'folders';
    ids: string[];
  } | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({ current: 0, total: 0 });
  // Cancellation for an in-flight bulk delete. The ref is read synchronously inside the
  // delete loop (state would be stale in the closure); the flag drives the "Stopping…" label.
  const cancelBulkRef = useRef(false);
  const [bulkDeleteCancelling, setBulkDeleteCancelling] = useState(false);

  // Always reflects the view currently shown (folder + search term + project/folder
  // scope), so a slow page-1 or select-all response can't apply to a view the user has
  // since changed — the same invariant usePaginatedList enforces for Load More.
  const scopeKeyRef = useRef(scopeKey);
  scopeKeyRef.current = scopeKey;
  // Guards loadPath (breadcrumb/description), which is navigation-scoped, not search-scoped.
  const currentFolderIdRef = useRef(currentFolderId);
  currentFolderIdRef.current = currentFolderId;

  // Load contents of current folder — resets to page 1 and replaces both
  // folders and files. Used on folder change and after any write that may
  // shift the listing (folder CRUD, bulk delete/move, upload completion).
  const { setFirstPage } = filePagination;
  const loadContents = useCallback(async () => {
    const requestScope = scopeKey;
    try {
      setLoading(true);
      setError('');

      // Load folders and files in parallel. Search matches folders too, so the
      // folder fetch carries the same term/scope: project scope searches every
      // folder (parentId omitted), folder scope lists/searches the current folder.
      const [foldersData, filesData] = await Promise.all([
        api.listFolders(
          projectId,
          isProjectScope ? undefined : (effectiveFolderId ?? undefined),
          { q: effectiveQ || undefined, scope: isProjectScope ? 'project' : 'folder' },
        ),
        fetchFilesPage(1),
      ]);

      // Bail if the view (folder / search term / scope) changed while in flight.
      if (scopeKeyRef.current !== requestScope) return;

      setFolders(foldersData.folders);
      setFirstPage(filesData);
      // Project-wide folder search is capped server-side; tell the user if it hit the cap.
      if (foldersData.truncated) {
        setError(
          `Showing the first ${foldersData.folders.length.toLocaleString()} matching folders; refine your search to see more.`,
        );
      }
    } catch (err: unknown) {
      if (scopeKeyRef.current !== requestScope) return;
      setError(getErrorMessage(err));
    } finally {
      if (scopeKeyRef.current === requestScope) setLoading(false);
    }
  }, [scopeKey, effectiveFolderId, projectId, fetchFilesPage, setFirstPage]);

  // Upload-complete refresh is debounced: a 50-file batch otherwise fires 50
  // sequential count+data queries. Trailing 500ms.
  const uploadCompleteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleUploadComplete = useCallback(() => {
    if (uploadCompleteTimerRef.current) {
      clearTimeout(uploadCompleteTimerRef.current);
    }
    uploadCompleteTimerRef.current = setTimeout(() => {
      uploadCompleteTimerRef.current = null;
      loadContents();
    }, 500);
  }, [loadContents]);

  useEffect(
    () => () => {
      if (uploadCompleteTimerRef.current) {
        clearTimeout(uploadCompleteTimerRef.current);
      }
    },
    [],
  );

  // Upload hook
  const upload = useUpload({
    targetFolderId: effectiveFolderId,
    projectId,
    onUploadComplete: handleUploadComplete,
  });

  // Load path when folder changes. Guarded on currentFolderId so a slow path
  // response can't overwrite the breadcrumb after the user navigated elsewhere.
  const loadPath = useCallback(async () => {
    const requested = currentFolderId;
    if (!currentFolderId) {
      setCurrentPath([]);
      // Load root folder description if rootFolderId is available
      if (rootFolderId) {
        try {
          const { folder } = await api.getFolder(rootFolderId);
          if (currentFolderIdRef.current !== requested) return;
          setCurrentFolderDescription(folder.description);
        } catch (err) {
          if (currentFolderIdRef.current !== requested) return;
          console.error('Failed to load root folder description:', err);
          setCurrentFolderDescription(null);
        }
      } else {
        setCurrentFolderDescription(null);
      }
      return;
    }

    try {
      const { folder, path } = await api.getFolder(currentFolderId);
      if (currentFolderIdRef.current !== requested) return;
      setCurrentPath(path);
      setCurrentFolderDescription(folder.description);
    } catch (err) {
      if (currentFolderIdRef.current !== requested) return;
      console.error('Failed to load path:', err);
      setCurrentPath([]);
      setCurrentFolderDescription(null);
    }
  }, [currentFolderId, rootFolderId]);

  useEffect(() => {
    loadContents();
  }, [loadContents]);

  // Breadcrumb/path is navigation-scoped — keep it off loadContents so it isn't
  // refetched on every debounced search change (loadContents now depends on the term).
  useEffect(() => {
    loadPath();
  }, [loadPath]);

  // Close upload dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (upload.uploadDropdownRef.current && !upload.uploadDropdownRef.current.contains(event.target as Node)) {
        upload.setShowUploadDropdown(false);
      }
    };

    if (upload.showUploadDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [upload.showUploadDropdown]);

  // Navigation. Entering a folder clears any active search and reverts to folder
  // scope, so the destination shows that folder's contents rather than a stale
  // (possibly project-wide) result set.
  const navigateToFolder = (folderId: string | null) => {
    setSearchRaw('');
    setScope('folder');
    onFolderChange(folderId);
    setSelectedFile(null);
  };

  const navigateToRoot = () => {
    navigateToFolder(null);
  };

  // From a project-wide search result, jump to the folder a match lives in. A
  // root-level match (folder_id null, or the project's __root__ folder) goes to root.
  const navigateToResultFolder = (folderId: string | null) => {
    navigateToFolder(folderId && folderId !== rootFolderId ? folderId : null);
  };

  // Folder actions
  const handleCreateFolderAndReturnId = async (name: string): Promise<string> => {
    const folder = await api.createFolder(name, projectId, effectiveFolderId ?? undefined);
    await loadContents();
    return folder.id;
  };

  const handleCreateFolderInline = async (name: string): Promise<void> => {
    await api.createFolder(name, projectId, effectiveFolderId ?? undefined);
    await loadContents();
    setIsCreatingNewFolder(false);
  };

  const handleRenameFolder = async (folderId: string, newName: string) => {
    // The PATCH itself must sit inside the busy guard — a mid-rename deploy
    // reload would otherwise race the commit. Let a rejected rename throw so
    // the inline editor stays open with the error; once it commits, drive the
    // S3 relocation like a move (a rename changes every descendant file's key).
    busyTracker.begin();
    try {
      const { rekey } = await api.renameFolder(folderId, newName);
      await runFolderMove('Renaming folder…', () =>
        driveFolderRekey(folderId, rekey, (relocated, total) =>
          setMovingMessage(`Renaming folder… (${relocated} of ${total} files relocated)`)
        )
      );
    } finally {
      busyTracker.end();
    }
  };

  const handleDeleteFolder = async () => {
    if (!deleteTarget || deleteTarget.type !== 'folder') return;

    setDeletingTarget(true);
    try {
      await api.deleteFolder(deleteTarget.item.id);
      await loadContents();
      setDeleteTarget(null);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      setDeleteTarget(null);
    } finally {
      setDeletingTarget(false);
    }
  };

  const handleUpdateFolderDescription = async (description: string | null) => {
    const folderId = currentFolderId || rootFolderId;
    if (!folderId) return;
    try {
      await api.updateFolder(folderId, { description });
      setCurrentFolderDescription(description);
    } catch (err: unknown) {
      throw err;
    }
  };

  // File actions
  const handleFileSelect = (file: FileUpload) => {
    setSelectedFile(selectedFile?.id === file.id ? null : file);
  };

  const handleUpdateFile = async (fileId: string, data: { description?: string }) => {
    try {
      const updated = await api.updateUpload(fileId, data);
      filePagination.setItems((prev) => prev.map((f) => (f.id === fileId ? updated : f)));
      if (selectedFile?.id === fileId) {
        setSelectedFile(updated);
      }
    } catch (err: unknown) {
      throw err;
    }
  };

  const handleRenameFile = async (fileId: string, newName: string) => {
    try {
      const updated = await api.updateUpload(fileId, { fileName: newName });
      filePagination.setItems((prev) => prev.map((f) => (f.id === fileId ? updated : f)));
      if (selectedFile?.id === fileId) {
        setSelectedFile(updated);
      }
    } catch (err: unknown) {
      throw err;
    }
  };

  const handleDeleteFile = async () => {
    if (!deleteTarget || deleteTarget.type !== 'file') return;

    setDeletingTarget(true);
    try {
      await api.deleteUpload(deleteTarget.item.id);
      filePagination.setItems((prev) => prev.filter((f) => f.id !== deleteTarget.item.id));
      filePagination.adjustTotal(-1);
      if (selectedFile?.id === deleteTarget.item.id) {
        setSelectedFile(null);
      }
      setDeleteTarget(null);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      setDeleteTarget(null);
    } finally {
      setDeletingTarget(false);
    }
  };

  // Drag and drop
  const handleFileDragStart = (_e: React.DragEvent, file: FileUpload) => {
    setDraggingFile(file);
  };

  const handleFileDragEnd = () => {
    setDraggingFile(null);
  };

  // Drop targets surface `null` when the drop lands on "Home" — always the project
  // root, not the folder currently in view. See resolveDropTarget for why this must
  // not fall back to effectiveFolderId (the "already there" guards below would then
  // silently no-op a drop-onto-Home from inside a subfolder).
  const resolveTarget = (targetFolderId: string | null): string | null =>
    resolveDropTarget(targetFolderId, rootFolderId);

  const handleFileDrop = async (targetFolderId: string | null, fileId: string) => {
    const resolvedTarget = resolveTarget(targetFolderId);
    const file = files.find((f) => f.id === fileId);
    if (!file || file.folder_id === resolvedTarget) {
      return;
    }

    try {
      await api.updateUpload(fileId, { folderId: resolvedTarget });
      filePagination.setItems((prev) => prev.filter((f) => f.id !== fileId));
      filePagination.adjustTotal(-1);
      if (selectedFile?.id === fileId) {
        setSelectedFile(null);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  };

  // Run a folder-move operation with the busy guard + "Moving…" overlay, then
  // refresh the listing. `move` performs the API call(s) and returns an error
  // message to surface (or null); it must not throw. We always refetch afterward
  // so the destination folder's subfolder count reflects whatever moves succeeded
  // — and we surface the error *after* loadContents, which clears `error` on entry.
  // busyTracker.begin/end brackets the whole thing so a mid-move deploy auto-refresh
  // or navigation defers until it finishes, exactly like a bulk upload/delete.
  const runFolderMove = async (message: string, move: () => Promise<string | null>) => {
    busyTracker.begin();
    setMovingMessage(message);
    let errorMessage: string | null = null;
    try {
      errorMessage = await move();
    } catch (err: unknown) {
      errorMessage = getErrorMessage(err);
    }
    try {
      await loadContents();
      if (errorMessage) setError(errorMessage);
    } finally {
      setMovingMessage(null);
      busyTracker.end();
    }
  };

  // A move/rename commits its DB change on the PATCH, but the matching S3
  // relocation happens in bounded server-side chunks (the PATCH does one, each
  // /rekey call does one more) so no single request can hit the Lambda timeout
  // on a large subtree. Drive the remaining chunks to completion. Returns an
  // error message to surface, or null; never throws. Each chunk gets a few
  // attempts (transient blips must not abort the run); a chunk that still
  // fails — including the server's no-progress stall error — ends the loop.
  // An `interrupted` initial status means the PATCH's inline chunk blew up
  // before measuring anything: run the loop anyway and learn the real state
  // (including the progress denominator) from the first chunk's response.
  const driveFolderRekey = async (
    folderId: string,
    initial: FolderRekeyStatus | undefined,
    onProgress?: (relocated: number, total: number) => void
  ): Promise<string | null> => {
    if (!initial) return null;
    if (!initial.interrupted && initial.stale - initial.moved === 0) return null;
    let relocated = initial.moved;
    let total = initial.stale;
    try {
      for (let round = 0; round < REKEY_MAX_ROUNDS; round++) {
        if (total > 0) onProgress?.(relocated, total);
        let batch: Awaited<ReturnType<typeof api.rekeyFolder>>;
        for (let attempt = 1; ; attempt++) {
          try {
            batch = await api.rekeyFolder(folderId);
            break;
          } catch (err: unknown) {
            if (attempt >= REKEY_CHUNK_ATTEMPTS) throw err;
            await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
          }
        }
        if (total === 0) total = relocated + batch.stale;
        relocated += batch.moved;
        if (batch.done) return null;
      }
      return 'The folder moved, but relocating its files has not finished — drop it onto its current location to resume.';
    } catch (err: unknown) {
      return getErrorMessage(err);
    }
  };

  const handleFolderDrop = async (targetFolderId: string | null, movedFolderId: string) => {
    const resolvedTarget = resolveTarget(targetFolderId);
    const folder = folders.find((f) => f.id === movedFolderId);
    if (!folder || movedFolderId === resolvedTarget) {
      return;
    }
    if (folder.parent_id === resolvedTarget) {
      // Dropping a folder onto its current location resumes a pending S3
      // relocation (the repair path after an interrupted move). The synthetic
      // `interrupted` status forces at least one server round, which no-ops
      // cheaply when the subtree is already consistent.
      await runFolderMove('Checking folder files…', () =>
        driveFolderRekey(
          movedFolderId,
          { total: 0, stale: 0, moved: 0, failed: 0, interrupted: true },
          (relocated, total) =>
            setMovingMessage(`Relocating files… (${relocated} of ${total})`)
        )
      );
      return;
    }

    await runFolderMove('Moving folder…', async () => {
      try {
        const { rekey } = await api.moveFolder(movedFolderId, resolvedTarget);
        return await driveFolderRekey(movedFolderId, rekey, (relocated, total) =>
          setMovingMessage(`Moving folder… (${relocated} of ${total} files relocated)`)
        );
      } catch (err: unknown) {
        return getErrorMessage(err);
      }
    });
  };

  // Close details panel when multi-select becomes active
  useEffect(() => {
    if (fileSelection.hasSelection) {
      setSelectedFile(null);
    }
  }, [fileSelection.hasSelection]);

  // Select every file matching the current view (across all pages), not just the
  // loaded ones — honoring the active search term and scope.
  const handleSelectAllFiles = async () => {
    const requestScope = scopeKey;
    try {
      const { ids, truncated } = await api.listUploadIds(
        projectId,
        isProjectScope ? undefined : (effectiveFolderId ?? undefined),
        { q: effectiveQ || undefined, scope: isProjectScope ? 'project' : 'folder' },
      );
      // Bail if the view (folder / search term / scope) changed while in flight — otherwise
      // a stale id set could become the selection, and a follow-up bulk delete (which deletes
      // by id regardless of the view) would remove files no longer shown. Same guard as loadContents.
      if (scopeKeyRef.current !== requestScope) return;
      setSelectableFileIds(new Set(ids));
      fileSelection.selectAllIds(ids);
      if (truncated) {
        setError(`Selected the first ${ids.length.toLocaleString()} files; there are more.`);
      }
    } catch (err: unknown) {
      if (scopeKeyRef.current !== requestScope) return;
      setError(getErrorMessage(err));
    }
  };

  // Bulk delete handlers
  const handleBulkDeleteFiles = () => {
    if (fileSelection.selectionCount === 0) return;
    setBulkDeleteTarget({ type: 'files', ids: [...fileSelection.selectedIds] });
  };

  const handleBulkDeleteFolders = () => {
    if (folderSelection.selectionCount === 0) return;
    setBulkDeleteTarget({ type: 'folders', ids: [...folderSelection.selectedIds] });
  };

  // The bulk-delete modal's Cancel button: while a delete is running it requests a stop (the
  // loop breaks before the next item — items already deleted stay deleted); otherwise it just
  // closes the confirmation dialog.
  const handleCancelBulkDelete = () => {
    if (bulkDeleting) {
      cancelBulkRef.current = true;
      setBulkDeleteCancelling(true);
    } else {
      setBulkDeleteTarget(null);
    }
  };

  const executeBulkDelete = async () => {
    if (!bulkDeleteTarget) return;

    const { type } = bulkDeleteTarget;

    // Only empty folders can be deleted (the API rejects non-empty ones with a
    // 400). Filter those out up front so they're reported as "skipped" — matching
    // the modal copy — rather than firing doomed requests and surfacing them as
    // delete failures. Files are never skipped.
    let ids = bulkDeleteTarget.ids;
    let skippedCount = 0;
    if (type === 'folders') {
      const deletable: string[] = [];
      for (const id of ids) {
        const folder = folders.find((f) => f.id === id);
        if (folder && (folder.file_count > 0 || folder.subfolder_count > 0)) {
          skippedCount++;
        } else {
          deletable.push(id);
        }
      }
      ids = deletable;
    }

    // A bulk delete can run for a long time (many files / sequential folders), so mark a
    // busy operation: a deploy auto-refresh and the reload/nav guards defer until it
    // finishes, exactly like a bulk upload (see busyTracker/reloadGate).
    busyTracker.begin();
    cancelBulkRef.current = false;
    setBulkDeleteCancelling(false);
    setBulkDeleting(true);
    setBulkDeleteProgress({ current: 0, total: ids.length });
    const errors: string[] = [];
    // How many ids the loop actually reached. The cancel flag is only checked at the top of
    // each iteration, so the in-flight batch/folder always finishes — this counts what was
    // processed so we don't report a "stopped early" when nothing was actually left undone.
    let processed = 0;

    try {
      if (type === 'files') {
        // Batched delete: one request per up-to-256 ids instead of one per file.
        for (const batch of chunk(ids, 256)) {
          if (cancelBulkRef.current) break; // user stopped the operation
          try {
            const results = await api.deleteUploadsBatch(batch);
            for (const r of results) {
              if (r.status !== 'deleted') {
                errors.push(r.message || `Failed to delete ${r.fileName ?? r.uploadId}`);
              }
            }
          } catch (err: unknown) {
            errors.push(getErrorMessage(err));
          }
          processed += batch.length;
          setBulkDeleteProgress({ current: processed, total: ids.length });
        }
      } else {
        // Folders stay sequential (heavier; each moves/checks its contents).
        for (let i = 0; i < ids.length; i++) {
          if (cancelBulkRef.current) break; // user stopped the operation
          setBulkDeleteProgress({ current: i + 1, total: ids.length });
          try {
            await api.deleteFolder(ids[i]);
          } catch (err: unknown) {
            errors.push(getErrorMessage(err));
          }
          processed += 1;
        }
      }

      await loadContents();

      if (type === 'files') {
        fileSelection.clearSelection();
        setSelectableFileIds(null);
        if (selectedFile && ids.includes(selectedFile.id)) {
          setSelectedFile(null);
        }
      } else {
        folderSelection.clearSelection();
      }
    } finally {
      // Release the busy guard (and reset UI) even if loadContents throws, so the reload/nav
      // guards never stay blocked.
      busyTracker.end();
      setBulkDeleting(false);
      setBulkDeleteCancelling(false);
      setBulkDeleteTarget(null);
      setBulkDeleteProgress({ current: 0, total: 0 });
    }

    const messages: string[] = [];
    // Only report a stop when items were genuinely left unprocessed. A cancel requested during
    // the final (or only) batch/folder still completes that unit, so nothing remains undone —
    // don't claim it halted early in that case.
    const unprocessed = ids.length - processed;
    if (cancelBulkRef.current && unprocessed > 0) {
      messages.push(`Delete stopped — ${unprocessed} item(s) were not deleted.`);
    }
    if (errors.length > 0) {
      messages.push(`${errors.length} item(s) failed to delete. ${errors[0]}`);
    }
    if (skippedCount > 0) {
      messages.push(`${skippedCount} folder(s) skipped because they aren't empty.`);
    }
    if (messages.length > 0) {
      setError(messages.join(' '));
    }
  };

  // Multi-move handlers. P10: build an id->item Map for O(1) lookup (was a
  // .find() per id, O(n*m)) and move with bounded concurrency instead of one
  // serial round-trip per item.
  const handleMultiFileDrop = async (targetFolderId: string | null, fileIds: string[]) => {
    const resolvedTarget = resolveTarget(targetFolderId);
    const byId = new Map(files.map((f) => [f.id, f]));
    const toMove = fileIds.filter((id) => {
      const file = byId.get(id);
      return file !== undefined && file.folder_id !== resolvedTarget;
    });

    const results = await mapWithConcurrency(toMove, MOVE_CONCURRENCY, async (fileId) => {
      try {
        await api.updateUpload(fileId, { folderId: resolvedTarget });
        return null;
      } catch (err: unknown) {
        return getErrorMessage(err);
      }
    });
    const errors = results.filter((r): r is string => r !== null);

    await loadContents();
    fileSelection.clearSelection();
    if (selectedFile && fileIds.includes(selectedFile.id)) {
      setSelectedFile(null);
    }
    if (errors.length > 0) {
      setError(`${errors.length} file(s) failed to move`);
    }
  };

  const handleMultiFolderDrop = async (targetFolderId: string | null, folderIds: string[]) => {
    const resolvedTarget = resolveTarget(targetFolderId);
    const byId = new Map(folders.map((f) => [f.id, f]));
    const toMove = folderIds.filter((id) => {
      const folder = byId.get(id);
      return folder !== undefined && folder.parent_id !== resolvedTarget && id !== resolvedTarget;
    });
    if (toMove.length === 0) {
      return;
    }

    await runFolderMove(
      toMove.length === 1 ? 'Moving folder…' : `Moving ${toMove.length} folders…`,
      async () => {
        const results = await mapWithConcurrency(toMove, MOVE_CONCURRENCY, async (folderId) => {
          try {
            const { rekey } = await api.moveFolder(folderId, resolvedTarget);
            return await driveFolderRekey(folderId, rekey);
          } catch (err: unknown) {
            return getErrorMessage(err);
          }
        });
        const errors = results.filter((r): r is string => r !== null);
        return errors.length > 0 ? `${errors.length} folder(s) reported errors while moving. ${errors[0]}` : null;
      },
    );
    folderSelection.clearSelection();
  };

  if (error && !loading && folders.length === 0 && files.length === 0) {
    return (
      <div className="file-browser">
        <div className="file-browser-error">
          <p>{error}</p>
          <button className="btn-primary" onClick={loadContents}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="file-browser">
      {/* Header with breadcrumb and actions */}
      <div className="file-browser-header">
        <Breadcrumb
          path={currentPath}
          onNavigate={navigateToFolder}
          onNavigateRoot={navigateToRoot}
          onFileDrop={handleFileDrop}
          onFolderDrop={handleFolderDrop}
          onMultiFileDrop={handleMultiFileDrop}
          onMultiFolderDrop={handleMultiFolderDrop}
          onCreateFolder={handleCreateFolderAndReturnId}
        />
      </div>

      {/* Content area - now a drop zone */}
      <div
        className={`file-browser-content ${selectedFile ? 'with-panel' : ''} ${upload.draggingUpload ? 'drop-active' : ''}`}
        onDragOver={upload.handleUploadDragOver}
        onDragEnter={upload.handleUploadDragEnter}
        onDragLeave={upload.handleUploadDragLeave}
        onDrop={upload.handleUploadDrop}
      >
        {movingMessage && (
          <div className="file-browser-moving-overlay" role="status" aria-live="polite">
            <LoadingSpinner message={movingMessage} />
          </div>
        )}
        <div className="file-browser-main">
          {/* Filename search */}
          <div className="file-browser-search">
            <div className="search-input-wrap">
              <svg
                className="search-input-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="search"
                className="search-input"
                placeholder="Search files and folders…"
                value={searchRaw}
                onChange={(e) => setSearchRaw(e.target.value)}
                aria-label="Search files and folders by name"
              />
              {searchRaw && (
                <button
                  type="button"
                  className="search-clear"
                  onClick={() => setSearchRaw('')}
                  title="Clear search"
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            <label className="search-scope-toggle">
              <input
                type="checkbox"
                checked={scope === 'project'}
                onChange={(e) => setScope(e.target.checked ? 'project' : 'folder')}
              />
              <span>Search entire project</span>
            </label>
            {searchRaw.trim().length === 1 && (
              <span className="search-hint">Type at least 2 characters…</span>
            )}
          </div>

          {/* Toolbar */}
          <div className="file-browser-toolbar">
            {(currentFolderId || rootFolderId) && (
              <div className="toolbar-description">
                <FolderDescription description={currentFolderDescription} onSave={handleUpdateFolderDescription} />
              </div>
            )}

            <div className="toolbar-actions">
              <div className="upload-dropdown" ref={upload.uploadDropdownRef}>
                <button
                  className="icon-btn"
                  onClick={() => upload.setShowUploadDropdown(!upload.showUploadDropdown)}
                  title="Upload"
                  disabled={upload.uploading}
                >
                  <UploadIcon />
                </button>
                {upload.showUploadDropdown && (
                  <div className="upload-dropdown-menu">
                    <button
                      className="upload-dropdown-item"
                      onClick={() => {
                        upload.fileInputRef.current?.click();
                        upload.setShowUploadDropdown(false);
                      }}
                    >
                      <FileIcon />
                      <span>Add Files</span>
                    </button>
                    <button
                      className="upload-dropdown-item"
                      onClick={() => {
                        upload.folderInputRef.current?.click();
                        upload.setShowUploadDropdown(false);
                      }}
                    >
                      <FolderIcon />
                      <span>Add Folder</span>
                    </button>
                  </div>
                )}
              </div>
              <button
                className="icon-btn"
                onClick={() => setIsCreatingNewFolder(true)}
                title="New folder"
              >
                <FolderPlusIcon />
              </button>
            </div>
          </div>

          {/* Hidden file input */}
          <input
            ref={upload.fileInputRef}
            type="file"
            accept={ALLOWED_EXTENSIONS.join(',')}
            onChange={upload.handleFileInputChange}
            multiple
            className="hidden-input"
          />
          {/* Hidden folder input */}
          <input
            ref={upload.folderInputRef}
            type="file"
            /* @ts-ignore webkitdirectory is non-standard but widely supported */
            {...{ webkitdirectory: '' } as React.InputHTMLAttributes<HTMLInputElement>}
            onChange={upload.handleFolderInputChange}
            className="hidden-input"
          />
          {loading ? (
            <div className="file-browser-loading">
              <LoadingSpinner message="Loading..." />
            </div>
          ) : (
            <>
              {error && <div className="file-browser-inline-error">{error}</div>}

              <UploadPanels
                pendingFiles={upload.pendingFiles}
                uploading={upload.uploading}
                uploadProgress={upload.uploadProgress}
                uploadResults={upload.uploadResults}
                resultsVisible={upload.uploadResultsVisible}
                onRemovePendingFile={upload.removePendingFile}
                onClearPendingFiles={upload.clearPendingFiles}
                onUploadPendingFiles={upload.uploadPendingFiles}
                onClearUploadResults={upload.clearUploadResults}
              />

              {/* Folders — searched alongside files; the inline "new folder" input
                  is only offered when not searching. */}
              {(folders.length > 0 || (!isSearching && isCreatingNewFolder)) && (
                <FolderList
                  searchTerm={effectiveQ}
                  onNavigateToFolder={isProjectScope ? navigateToResultFolder : undefined}
                  folders={folders}
                  onOpen={navigateToFolder}
                  onRename={handleRenameFolder}
                  onDelete={(folder) => setDeleteTarget({ type: 'folder', item: folder })}
                  onFileDrop={handleFileDrop}
                  onFolderDrop={handleFolderDrop}
                  onMultiFileDrop={handleMultiFileDrop}
                  onMultiFolderDrop={handleMultiFolderDrop}
                  isCreatingNew={!isSearching && isCreatingNewFolder}
                  onCreateNew={handleCreateFolderInline}
                  onCancelCreate={() => setIsCreatingNewFolder(false)}
                  multiSelectedIds={folderSelection.selectedIds}
                  hasMultiSelection={folderSelection.hasSelection}
                  onMultiSelectToggle={(id, event) => folderSelection.toggle(id, event)}
                  onSelectAll={folderSelection.selectAll}
                  onClearSelection={folderSelection.clearSelection}
                  onDeleteSelected={handleBulkDeleteFolders}
                  deletingSelected={bulkDeleting && bulkDeleteTarget?.type === 'folders'}
                />
              )}

              {/* Files */}
              {files.length > 0 && (
                <FileList
                  files={files}
                  selectedFileId={selectedFile?.id}
                  onSelect={handleFileSelect}
                  onRename={handleRenameFile}
                  onDelete={(file) => setDeleteTarget({ type: 'file', item: file })}
                  onDragStart={handleFileDragStart}
                  onDragEnd={handleFileDragEnd}
                  multiSelectedIds={fileSelection.selectedIds}
                  hasMultiSelection={fileSelection.hasSelection}
                  onMultiSelectToggle={(id, event) => fileSelection.toggle(id, event)}
                  onSelectAll={handleSelectAllFiles}
                  onClearSelection={fileSelection.clearSelection}
                  onDeleteSelected={handleBulkDeleteFiles}
                  deletingSelected={bulkDeleting && bulkDeleteTarget?.type === 'files'}
                  totalFiles={filePagination.total}
                  hasMore={filePagination.hasMore}
                  isLoadingMore={filePagination.isLoadingMore}
                  loadMoreError={filePagination.loadMoreError}
                  onLoadMore={filePagination.loadMore}
                  searchTerm={effectiveQ}
                  onNavigateToFolder={isProjectScope ? navigateToResultFolder : undefined}
                />
              )}

              {/* No-results state for an active search (no matching files OR folders) */}
              {isSearching && files.length === 0 && folders.length === 0 && !loading && (
                <div className="file-browser-empty">
                  <div className="empty-icon">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="11" cy="11" r="7" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </div>
                  <p>
                    No files or folders match “{effectiveQ}”{isProjectScope ? ' in this project' : ' in this folder'}
                  </p>
                  <p className="empty-hint">
                    {isProjectScope
                      ? 'Try a different term.'
                      : 'Try “Search entire project” to look beyond this folder.'}
                  </p>
                </div>
              )}

              {/* Empty state (no search active) */}
              {!isSearching && folders.length === 0 && files.length === 0 && !loading && (
                <div className="file-browser-empty">
                  <div className="empty-icon">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                  </div>
                  <p>This folder is empty</p>
                  <p className="empty-hint">Drop files anywhere or use the toolbar above</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* File details panel */}
        {selectedFile && (
          <FileDetailsPanel file={selectedFile} onClose={() => setSelectedFile(null)} onUpdate={handleUpdateFile} />
        )}
      </div>

      <DeleteConfirmModals
        deleteTarget={deleteTarget}
        deleting={deletingTarget}
        onCancelDelete={() => setDeleteTarget(null)}
        onDeleteFolder={handleDeleteFolder}
        onDeleteFile={handleDeleteFile}
        bulkDeleteTarget={bulkDeleteTarget}
        bulkDeleting={bulkDeleting}
        bulkDeleteCancelling={bulkDeleteCancelling}
        bulkDeleteProgress={bulkDeleteProgress}
        onCancelBulkDelete={handleCancelBulkDelete}
        onExecuteBulkDelete={executeBulkDelete}
      />
    </div>
  );
}
